// ============================================================================
// src/lib/appStoreConfigOperationApi.ts — the client of public.app_store_config_operation
// ----------------------------------------------------------------------------
// Migration 249 (R-I: remaining 9 client-written public.app_store keys) moves
// the last direct client app_store writes onto ONE audited SECURITY DEFINER
// entrypoint with private immutable request receipts in
// public.app_store_config_operation_receipts:
//
//   * `feed_orders.patch`             (ppp-feed-orders-v1)
//   * `poultry_feed_inventory.save`   (ppp-poultry-feed-inventory-v1)
//   * `pig_feed_inventory.save`       (ppp-pig-feed-inventory-v1)
//   * `pig_breed_options.add`         (ppp-breed-options-v1)
//   * `pig_origin_options.add`        (ppp-origin-options-v1)
//   * `pig_global_adg.save`           (ppp-pig-global-adg-v1)
//   * `pig_planned_trip_locks.save`   (ppp-pig-planned-trip-locks-v1)
//   * `webforms_config.save`          (ppp-webforms-v1)
//   * `feed_costs.save`               (ppp-feed-costs-v1)
//
// Transport posture matches src/lib/appStoreOperationApi.ts (migration 246):
//   * One requestId per logical action; transport timeouts/network errors retry
//     up to 3 times (8s race, 1s/2s backoff) with the SAME requestId so the
//     server receipt deduplicates.
//   * A database SQLSTATE refusal (22023, 42501, WCF01, ...) is an authoritative
//     answer and returns immediately without retry.
// ============================================================================

import type {SaveStatusSink} from './appStoreBlobApi.js';
import {reportAppStoreSaveStatus} from './appStoreOperationApi.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import {
  FEED_CONFIG_KEYS,
  applyFeedFenceOperation,
  freezeFeedFenceRequest,
  type FeedConfigOperation,
  type FeedFenceOutcome,
  type JsonObject,
} from './feedReceiptOperationApi.js';
import type {SupabaseClient} from './supabaseTypes.js';

export type {SaveStatusSink};

export type AppStoreConfigFenceOptions = {
  expectedActor?: string | null;
  beforeAttempt?: () => Promise<boolean> | boolean;
};

const ACTIVE_REQUEST_FENCES = new Map<string, AppStoreConfigFenceOptions>();

export function getActiveFeedRequestFence(requestId?: string | null): AppStoreConfigFenceOptions | undefined {
  if (!requestId) return undefined;
  return ACTIVE_REQUEST_FENCES.get(requestId);
}

export async function withFeedRequestFence<T>(
  requestId: string,
  fence: AppStoreConfigFenceOptions | undefined,
  fn: () => Promise<T>,
): Promise<T> {
  if (!fence || !requestId) return fn();
  ACTIVE_REQUEST_FENCES.set(requestId, fence);
  try {
    return await fn();
  } finally {
    if (ACTIVE_REQUEST_FENCES.get(requestId) === fence) {
      ACTIVE_REQUEST_FENCES.delete(requestId);
    }
  }
}

export const FEED_ORDERS_APP_STORE_KEY = 'ppp-feed-orders-v1' as const;
export const POULTRY_FEED_INVENTORY_APP_STORE_KEY = 'ppp-poultry-feed-inventory-v1' as const;
export const PIG_FEED_INVENTORY_APP_STORE_KEY = 'ppp-pig-feed-inventory-v1' as const;
export const PIG_BREED_OPTIONS_APP_STORE_KEY = 'ppp-breed-options-v1' as const;
export const PIG_ORIGIN_OPTIONS_APP_STORE_KEY = 'ppp-origin-options-v1' as const;
export const PIG_GLOBAL_ADG_APP_STORE_KEY = 'ppp-pig-global-adg-v1' as const;
export const PIG_PLANNED_TRIP_LOCKS_APP_STORE_KEY = 'ppp-pig-planned-trip-locks-v1' as const;
export const WEBFORMS_CONFIG_APP_STORE_KEY = 'ppp-webforms-v1' as const;
export const FEED_COSTS_APP_STORE_KEY = 'ppp-feed-costs-v1' as const;

export type AppStoreConfigOperation =
  | 'feed_orders.patch'
  | 'poultry_feed_inventory.save'
  | 'pig_feed_inventory.save'
  | 'pig_breed_options.add'
  | 'pig_origin_options.add'
  | 'pig_global_adg.save'
  | 'pig_planned_trip_locks.save'
  | 'webforms_config.save'
  | 'feed_costs.save';

export type PoultryFeedInventoryEntryPayload = {
  count: number;
  date: string;
  includesCurrentMonthDelivery: boolean;
};

export type PigFeedInventoryPayload = {
  count: number;
  date: string;
  includesCurrentMonthDelivery: boolean;
};

export type PigGlobalAdgPayload = {
  manualValue: number | string | null;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type PlannedTripLockPayload = {
  locked?: boolean;
  lockedByName?: string | null;
  lockedByUserId?: string | null;
  lockedAt?: string | null;
};

export type FeedCostsPayload = Record<string, unknown>;

export type AppStoreConfigOpRequest =
  | {
      requestId: string;
      operation: 'feed_orders.patch';
      target: {key: typeof FEED_ORDERS_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {patch: Record<string, Record<string, number | null>>};
    }
  | {
      requestId: string;
      operation: 'poultry_feed_inventory.save';
      target: {key: typeof POULTRY_FEED_INVENTORY_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {
        type?: string;
        entry?: PoultryFeedInventoryEntryPayload;
        data?: Record<string, PoultryFeedInventoryEntryPayload | undefined>;
      };
    }
  | {
      requestId: string;
      operation: 'pig_feed_inventory.save';
      target: {key: typeof PIG_FEED_INVENTORY_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {data: PigFeedInventoryPayload};
    }
  | {
      requestId: string;
      operation: 'pig_breed_options.add';
      target: {key: typeof PIG_BREED_OPTIONS_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {value: string; options?: string[]};
    }
  | {
      requestId: string;
      operation: 'pig_origin_options.add';
      target: {key: typeof PIG_ORIGIN_OPTIONS_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {value: string; options?: string[]};
    }
  | {
      requestId: string;
      operation: 'pig_global_adg.save';
      target: {key: typeof PIG_GLOBAL_ADG_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {data: PigGlobalAdgPayload};
    }
  | {
      requestId: string;
      operation: 'pig_planned_trip_locks.save';
      target: {key: typeof PIG_PLANNED_TRIP_LOCKS_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {
        trip_id?: string;
        lock?: PlannedTripLockPayload | null;
        data?: Record<string, PlannedTripLockPayload>;
      };
    }
  | {
      requestId: string;
      operation: 'webforms_config.save';
      target: {key: typeof WEBFORMS_CONFIG_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {data: unknown};
    }
  | {
      requestId: string;
      operation: 'feed_costs.save';
      target: {key: typeof FEED_COSTS_APP_STORE_KEY};
      expected?: Record<string, never>;
      change: {data: FeedCostsPayload};
    };

export type AppStoreConfigOpOutcome =
  | {
      status: 'saved';
      written: true;
      key: string | null;
      data: unknown;
      updatedAt: string | null;
      replayed: boolean;
    }
  | {
      status: 'no_change';
      written: false;
      key: string | null;
      data: unknown;
      updatedAt: string | null;
      replayed: boolean;
    }
  | {
      status: 'refused';
      reason: string;
      key: string | null;
      replayed: boolean;
    }
  | {
      status: 'rejected';
      code: string;
      message: string;
    }
  | {
      status: 'unknown';
      message: string;
      mustReread: true;
    }
  | {
      status: 'not_sent';
      message: string;
    };

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string} | null;

/** A fresh request id for one logical app_store_config_operation call. */
export function newAppStoreConfigRequestId(prefix = 'appstore-cfg'): string {
  return `${prefix}-${newClientSubmissionId()}`;
}

/** Interpret one reply from public.app_store_config_operation. */
export function interpretAppStoreConfigReply(request: AppStoreConfigOpRequest, data: unknown): AppStoreConfigOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
  }
  switch (d.kind) {
    case 'saved': {
      if (d.ok !== true) break;
      return {
        status: 'saved',
        written: true,
        key: typeof d.key === 'string' ? d.key : null,
        data: d.data ?? null,
        updatedAt: typeof d.updated_at === 'string' ? d.updated_at : null,
        replayed: d.replayed === true,
      };
    }
    case 'no_change': {
      if (d.ok !== true) break;
      return {
        status: 'no_change',
        written: false,
        key: typeof d.key === 'string' ? d.key : null,
        data: d.data ?? null,
        updatedAt: typeof d.updated_at === 'string' ? d.updated_at : null,
        replayed: d.replayed === true,
      };
    }
    case 'refused': {
      return {
        status: 'refused',
        reason: typeof d.reason === 'string' ? d.reason : 'refused',
        key: typeof d.key === 'string' ? d.key : null,
        replayed: d.replayed === true,
      };
    }
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
}

function isFeedConfigOperation(operation: AppStoreConfigOperation): operation is FeedConfigOperation {
  return Object.hasOwn(FEED_CONFIG_KEYS, operation);
}

function mapFeedFenceToConfigOutcome(out: FeedFenceOutcome): AppStoreConfigOpOutcome {
  switch (out.status) {
    case 'not_sent':
      return {status: 'not_sent', message: out.problem};
    case 'settled': {
      const {receipt} = out;
      const detail =
        receipt.detail && typeof receipt.detail === 'object' && !Array.isArray(receipt.detail)
          ? (receipt.detail as Record<string, unknown>)
          : null;
      const key = detail && typeof detail.key === 'string' ? detail.key : (receipt.targets.key as string);
      const data =
        detail && 'data' in detail
          ? (detail.data ?? null)
          : 'data' in receipt.change
            ? (receipt.change.data ?? null)
            : null;
      const updatedAt = detail && typeof detail.updated_at === 'string' ? detail.updated_at : null;
      if (receipt.outcome === 'applied') {
        return {
          status: 'saved',
          written: true,
          key,
          data,
          updatedAt,
          replayed: receipt.replayed,
        };
      }
      if (receipt.outcome === 'no_change') {
        return {
          status: 'no_change',
          written: false,
          key,
          data,
          updatedAt,
          replayed: receipt.replayed,
        };
      }
      const reason =
        detail && typeof detail.reason === 'string'
          ? detail.reason
          : typeof receipt.detail === 'string'
            ? receipt.detail
            : 'refused';
      return {
        status: 'refused',
        reason,
        key,
        replayed: receipt.replayed,
      };
    }
    case 'denied':
    case 'rejected':
      return {status: 'rejected', code: out.code, message: out.message};
    case 'unavailable':
    case 'unknown':
      return {status: 'unknown', message: out.message, mustReread: true};
  }
}

async function resolveSessionActor(sb: SupabaseClient): Promise<string> {
  const auth = (
    sb as unknown as {
      auth?: {getSession?: () => Promise<{data?: {session?: {user?: {id?: unknown}} | null}} | null>};
    }
  )?.auth;
  if (!auth || typeof auth.getSession !== 'function') return '';
  try {
    const res = await auth.getSession();
    const id = res?.data?.session?.user?.id;
    return typeof id === 'string' ? id.trim() : '';
  } catch {
    return '';
  }
}

/**
 * Execute one config operation. The four feed config operations
 * (`feed_orders.patch`, `poultry_feed_inventory.save`, `pig_feed_inventory.save`,
 * `feed_costs.save`) execute exclusively through `public.feed_operation_fenced`
 * (migration 260) with no legacy fallback; the five non-feed config operations
 * execute through `public.app_store_config_operation`.
 */
export async function applyAppStoreConfigOperation(
  sb: SupabaseClient,
  request: AppStoreConfigOpRequest,
  fenceArg?: AppStoreConfigFenceOptions,
): Promise<AppStoreConfigOpOutcome> {
  const fence = fenceArg ?? getActiveFeedRequestFence(request.requestId);
  if (isFeedConfigOperation(request.operation)) {
    const expectedActor =
      fence && 'expectedActor' in fence ? (fence.expectedActor ?? '') : await resolveSessionActor(sb);
    const frozen = freezeFeedFenceRequest(
      {
        family: 'config',
        operation: request.operation,
        targets: {key: request.target.key},
        expected: {},
        change: (request.change ?? {}) as JsonObject,
      },
      {
        requestId: request.requestId,
        expectedActor,
      },
    );
    if (!frozen.ok) {
      return {status: 'not_sent', message: frozen.problem};
    }
    const out = await applyFeedFenceOperation(sb, frozen.request, {
      beforeAttempt: fence?.beforeAttempt,
    });
    return mapFeedFenceToConfigOutcome(out);
  }

  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('app_store_config_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretAppStoreConfigReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      console.warn(`app_store_config_operation attempt ${attempt} failed for ${request.operation}:`, lastMessage);
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.error('app_store_config_operation failed after 3 attempts:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** Map a SQLSTATE rejection to a user-facing message. */
export function appStoreConfigRejectionMessage(
  outcome: Extract<AppStoreConfigOpOutcome, {status: 'rejected'}>,
): string {
  switch (outcome.code) {
    case '22023':
      return outcome.message.replace(/^(app_store_config_operation|feed_operation_fenced):\s*/, '');
    case '42501':
      return 'Your session or role changed. Sign in again; your change is kept.';
    case 'WCF01':
      return 'This change no longer matches what was saved. Reload and try again.';
    default:
      return outcome.message || 'The change was refused.';
  }
}

/**
 * Execute one `app_store_config_operation` with `onSaveStatus` reporting
 * (`saving` -> `saved` / `error` with 2.5s / 4s auto-clear timers) and optional
 * `onPersisted` callback before `saved`.
 */
export async function runAppStoreConfigOperationWithStatus(
  sb: SupabaseClient,
  request: AppStoreConfigOpRequest,
  onSaveStatus: SaveStatusSink,
  onPersisted?: (() => void) | AppStoreConfigFenceOptions,
  fenceArg?: AppStoreConfigFenceOptions,
): Promise<AppStoreConfigOpOutcome> {
  const persistedCb = typeof onPersisted === 'function' ? onPersisted : undefined;
  const fence = typeof onPersisted === 'function' ? fenceArg : (onPersisted ?? fenceArg);
  onSaveStatus('saving');
  const outcome = await applyAppStoreConfigOperation(sb, request, fence);
  if (outcome.status === 'not_sent') {
    return outcome;
  }
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    try {
      persistedCb?.();
    } catch (refreshError) {
      console.warn('Saved app_store config document; refresh callback failed:', refreshError);
    }
    reportAppStoreSaveStatus(onSaveStatus, 'saved');
    return outcome;
  }
  reportAppStoreSaveStatus(onSaveStatus, 'error');
  return outcome;
}

/**
 * 1. `feed_orders.patch` (`ppp-feed-orders-v1`): server-side locked patch merge.
 */
export async function patchFeedOrdersOnServer(
  sb: SupabaseClient,
  patch: Record<string, Record<string, number | null>>,
  onSaveStatus: SaveStatusSink,
  requestId?: string,
  fence?: AppStoreConfigFenceOptions,
): Promise<AppStoreConfigOpOutcome> {
  return runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: requestId || newAppStoreConfigRequestId('feed-orders'),
      operation: 'feed_orders.patch',
      target: {key: FEED_ORDERS_APP_STORE_KEY},
      expected: {},
      change: {patch},
    },
    onSaveStatus,
    undefined,
    fence,
  );
}

/**
 * 2. `poultry_feed_inventory.save` (`ppp-poultry-feed-inventory-v1`): per-type
 * inventory merge under the key advisory lock.
 */
export async function savePoultryFeedInventoryOnServer(
  sb: SupabaseClient,
  params: {
    type: string;
    entry: PoultryFeedInventoryEntryPayload;
    data?: Record<string, PoultryFeedInventoryEntryPayload | undefined>;
    requestId?: string;
  },
  onSaveStatus: SaveStatusSink,
  fence?: AppStoreConfigFenceOptions,
): Promise<boolean> {
  const change: {
    type: string;
    entry: PoultryFeedInventoryEntryPayload;
    data?: Record<string, PoultryFeedInventoryEntryPayload | undefined>;
  } = {
    type: params.type,
    entry: params.entry,
  };
  if (params.data) change.data = params.data;
  const outcome = await runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: params.requestId || newAppStoreConfigRequestId('poultry-inv'),
      operation: 'poultry_feed_inventory.save',
      target: {key: POULTRY_FEED_INVENTORY_APP_STORE_KEY},
      expected: {},
      change,
    },
    onSaveStatus,
    undefined,
    fence,
  );
  return outcome.status === 'saved' || outcome.status === 'no_change';
}

/**
 * 3. `pig_feed_inventory.save` (`ppp-pig-feed-inventory-v1`): singleton count save.
 *
 * The typed form returns the operation's own outcome — the same request, the
 * same status reporting — for a caller that must tell a refusal (`rejected`,
 * with its SQLSTATE) from a write no reply confirmed (`unknown`, which only a
 * same-id retry can settle).
 */
export async function savePigFeedInventoryOnServerWithOutcome(
  sb: SupabaseClient,
  data: PigFeedInventoryPayload,
  onSaveStatus: SaveStatusSink,
  requestId?: string,
  fence?: AppStoreConfigFenceOptions,
): Promise<AppStoreConfigOpOutcome> {
  return runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: requestId || newAppStoreConfigRequestId('pig-inv'),
      operation: 'pig_feed_inventory.save',
      target: {key: PIG_FEED_INVENTORY_APP_STORE_KEY},
      expected: {},
      change: {data},
    },
    onSaveStatus,
    undefined,
    fence,
  );
}

/** The boolean form every existing caller uses: saved or no change. */
export async function savePigFeedInventoryOnServer(
  sb: SupabaseClient,
  data: PigFeedInventoryPayload,
  onSaveStatus: SaveStatusSink,
  requestId?: string,
  fence?: AppStoreConfigFenceOptions,
): Promise<boolean> {
  const outcome = await savePigFeedInventoryOnServerWithOutcome(sb, data, onSaveStatus, requestId, fence);
  return outcome.status === 'saved' || outcome.status === 'no_change';
}

/**
 * 4. `pig_breed_options.add` (`ppp-breed-options-v1`): server-side set-append
 * under the key advisory lock.
 */
export async function addPigBreedOptionOnServer(
  sb: SupabaseClient,
  params: {value: string; options?: string[]; requestId?: string},
  onSaveStatus: SaveStatusSink,
): Promise<boolean> {
  const change: {value: string; options?: string[]} = {value: params.value};
  if (params.options) change.options = params.options;
  const outcome = await runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: params.requestId || newAppStoreConfigRequestId('pig-breed-opt'),
      operation: 'pig_breed_options.add',
      target: {key: PIG_BREED_OPTIONS_APP_STORE_KEY},
      expected: {},
      change,
    },
    onSaveStatus,
  );
  return outcome.status === 'saved' || outcome.status === 'no_change';
}

/**
 * 5. `pig_origin_options.add` (`ppp-origin-options-v1`): server-side set-append
 * under the key advisory lock.
 */
export async function addPigOriginOptionOnServer(
  sb: SupabaseClient,
  params: {value: string; options?: string[]; requestId?: string},
  onSaveStatus: SaveStatusSink,
): Promise<boolean> {
  const change: {value: string; options?: string[]} = {value: params.value};
  if (params.options) change.options = params.options;
  const outcome = await runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: params.requestId || newAppStoreConfigRequestId('pig-origin-opt'),
      operation: 'pig_origin_options.add',
      target: {key: PIG_ORIGIN_OPTIONS_APP_STORE_KEY},
      expected: {},
      change,
    },
    onSaveStatus,
  );
  return outcome.status === 'saved' || outcome.status === 'no_change';
}

/**
 * 6. `pig_global_adg.save` (`ppp-pig-global-adg-v1`): singleton manual ADG save.
 * Returns `{data, error}` so `persistPigGlobalAdg` callers keep their exact
 * promise contract.
 */
export async function savePigGlobalAdgOnServer(
  sb: SupabaseClient,
  data: PigGlobalAdgPayload,
  requestId?: string,
): Promise<{data: unknown; error: {code?: string; message: string} | null}> {
  const outcome = await applyAppStoreConfigOperation(sb, {
    requestId: requestId || newAppStoreConfigRequestId('pig-adg'),
    operation: 'pig_global_adg.save',
    target: {key: PIG_GLOBAL_ADG_APP_STORE_KEY},
    expected: {},
    change: {data},
  });
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    return {data: outcome.data, error: null};
  }
  if (outcome.status === 'rejected') {
    return {data: null, error: {code: outcome.code, message: appStoreConfigRejectionMessage(outcome)}};
  }
  if (outcome.status === 'refused') {
    return {data: null, error: {message: outcome.reason}};
  }
  return {data: null, error: {message: outcome.message}};
}

/**
 * 7. `pig_planned_trip_locks.save` (`ppp-pig-planned-trip-locks-v1`): per-trip
 * lock set/delete under the key advisory lock. Returns `{data, error}` so
 * `usePigPlannedTrips` surfaces errors via `setNotice` as before.
 */
export async function savePigPlannedTripLocksOnServer(
  sb: SupabaseClient,
  params: {
    tripId?: string;
    lock?: PlannedTripLockPayload | null;
    data?: Record<string, PlannedTripLockPayload>;
    requestId?: string;
  },
): Promise<{data: unknown; error: {code?: string; message: string} | null}> {
  const change: {
    trip_id?: string;
    lock?: PlannedTripLockPayload | null;
    data?: Record<string, PlannedTripLockPayload>;
  } = {};
  if (params.tripId !== undefined) {
    change.trip_id = params.tripId;
    change.lock = params.lock ?? null;
  }
  if (params.data !== undefined) {
    change.data = params.data;
  }
  const outcome = await applyAppStoreConfigOperation(sb, {
    requestId: params.requestId || newAppStoreConfigRequestId('pig-trip-lock'),
    operation: 'pig_planned_trip_locks.save',
    target: {key: PIG_PLANNED_TRIP_LOCKS_APP_STORE_KEY},
    expected: {},
    change,
  });
  if (outcome.status === 'saved' || outcome.status === 'no_change') {
    return {data: outcome.data, error: null};
  }
  if (outcome.status === 'rejected') {
    return {data: null, error: {code: outcome.code, message: appStoreConfigRejectionMessage(outcome)}};
  }
  if (outcome.status === 'refused') {
    return {data: null, error: {message: outcome.reason}};
  }
  return {data: null, error: {message: outcome.message}};
}

/**
 * 8. `webforms_config.save` (`ppp-webforms-v1`): admin webforms config save.
 */
export async function saveWebformsConfigOnServer(
  sb: SupabaseClient,
  data: unknown,
  onSaveStatus: SaveStatusSink,
  onPersisted?: () => void,
  requestId?: string,
): Promise<boolean> {
  const outcome = await runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: requestId || newAppStoreConfigRequestId('webforms-cfg'),
      operation: 'webforms_config.save',
      target: {key: WEBFORMS_CONFIG_APP_STORE_KEY},
      expected: {},
      change: {data},
    },
    onSaveStatus,
    onPersisted,
  );
  return outcome.status === 'saved' || outcome.status === 'no_change';
}

/**
 * 9. `feed_costs.save` (`ppp-feed-costs-v1`): admin feed-costs save.
 */
export async function saveFeedCostsOnServer(
  sb: SupabaseClient,
  data: FeedCostsPayload,
  onSaveStatus: SaveStatusSink,
  onPersisted?: () => void,
  requestId?: string,
  fence?: AppStoreConfigFenceOptions,
): Promise<boolean> {
  const outcome = await runAppStoreConfigOperationWithStatus(
    sb,
    {
      requestId: requestId || newAppStoreConfigRequestId('feed-costs'),
      operation: 'feed_costs.save',
      target: {key: FEED_COSTS_APP_STORE_KEY},
      expected: {},
      change: {data},
    },
    onSaveStatus,
    onPersisted,
    fence,
  );
  return outcome.status === 'saved' || outcome.status === 'no_change';
}
